import * as React from "react";
import {Redirect} from "react-router-dom";

import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {markMinorConversions} from "@khan/analytics-learning-time";
import type {Step, UpdateStep, UserClass} from "@khan/auth-shared";

import BirthdayAndSignupSubform from "#auth/components/birthday-and-signup-subform.tsx";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";
import LoginActionScreen from "#shared/auth/login/login-action-screen.tsx";
import ProgressShellActionScreen from "#shared/auth/progress-shell/progress-shell-action-screen.tsx";
import ProgressShell from "#shared/auth/progress-shell/progress-shell.tsx";
import SignupIdentifyActionScreen from "#shared/auth/signup/signup-identify-action-screen.tsx";

import JoinActionScreen from "./join-action-screen.tsx";
import JoinConfirmClassActionScreen from "./join-confirm-class-action-screen.tsx";
import JoinDetailScreen from "./join-detail-screen.tsx";
import JoinPageLayout from "./join-page-layout.tsx";

type Props = {
    className?: string;
    classSignupCode?: string;
    classIncludesCentrallyOwnedDistrictData?: boolean;
    tosFlag?: boolean;
};

type State = {
    userClass?: UserClass;
    birthdate: string;
    under13: boolean;
};

class JoinWidget extends React.Component<Props, State> {
    state: State = {
        userClass:
            this.props.className &&
            this.props.classSignupCode &&
            this.props.classIncludesCentrallyOwnedDistrictData != null
                ? {
                      name: this.props.className,
                      signupCode: this.props.classSignupCode,
                      includesCentrallyOwnedDistrictData:
                          this.props.classIncludesCentrallyOwnedDistrictData,
                  }
                : undefined,
        birthdate: "",
        under13: true,
    };

    handleBirthdateChange: (birthdate: string, under13: boolean) => void = (
        birthdate: string,
        under13: boolean,
    ): void => {
        this.setState({birthdate, under13});
    };

    handleClassChange: (userClass: UserClass) => void = (
        userClass: UserClass,
    ): void => {
        this.setState({userClass});
    };

    getClassCode: () => string = (): string => {
        return this.state.userClass ? this.state.userClass.signupCode : "";
    };

    renderDetailScreen: (step: Step) => React.ReactNode = (step) => {
        return <JoinDetailScreen step={step} />;
    };

    proceedToLogin: (updateStep: UpdateStep, code: string) => void = (
        updateStep: UpdateStep,
        code: string,
    ) => {
        markMinorConversions([
            {
                id: "activation_2.0_grassroots_login_clicked",
                extra: {
                    classCode: code,
                },
            },
        ]);
        updateStep("login");
    };

    proceedToSignUp: (updateStep: UpdateStep, code: string) => void = (
        updateStep: UpdateStep,
        code: string,
    ) => {
        markMinorConversions([
            {
                id: "activation_2.0_grassroots_signup_clicked",
                extra: {
                    classCode: code,
                },
            },
        ]);
        updateStep("signUp");
    };

    handleJoinDifferentClassClick: (
        updateStep: UpdateStep,
        code: string,
    ) => void = (updateStep: UpdateStep, code: string) => {
        markMinorConversions([
            {
                id: "activation_2.0_grassroots_different_class_clicked",
                extra: {
                    classCode: code,
                },
            },
        ]);
        this.setState({userClass: undefined});
        updateStep("intro");
    };

    renderActionScreen: (
        step: Step,
        updateStep: UpdateStep,
    ) => null | React.ReactElement<any> = (step, updateStep) => {
        const {birthdate, under13, userClass} = this.state;
        if (step === "signUp") {
            const useTosCheckboxFlag = this.props.tosFlag;

            // if the affirmative action checkbox flag is set we want the user to use the new flow
            if (useTosCheckboxFlag) {
                const classCode = this.getClassCode();

                let url = "/signup/learner/birthday";
                if (classCode) {
                    // confirm there is a class code
                    url += `?classcode=${classCode}`;
                }

                return <SmartRedirect to={url} />;
            }

            return (
                <ProgressShellActionScreen
                    onBackClick={() => updateStep("confirmClass")}
                    showBackButton={true}
                >
                    <BirthdayAndSignupSubform
                        classCode={this.getClassCode()}
                        continueUrl="/"
                        onBirthdateChange={this.handleBirthdateChange}
                        updateStep={updateStep}
                        showFacebookButton={false}
                    />
                </ProgressShellActionScreen>
            );
        }
        if (step === "confirmClass" && userClass) {
            const classToJoin = userClass;

            // If this is a Districts classroom, redirect to the Districts join
            // flow!
            if (classToJoin.includesCentrallyOwnedDistrictData) {
                const url = `/join/districts/${classToJoin.signupCode}`;
                return (
                    <>
                        <Redirect to={url} />
                        <View
                            style={{
                                flex: "1",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularSpinner size="medium" />
                        </View>
                    </>
                );
            }

            // Otherwise, proceed with the Grassroots join flow.
            return (
                <JoinConfirmClassActionScreen
                    classCode={classToJoin.signupCode}
                    className={classToJoin.name}
                    onJoinDifferentClass={() => {
                        this.handleJoinDifferentClassClick(
                            updateStep,
                            classToJoin.signupCode,
                        );
                    }}
                    onLoginWithCode={() =>
                        this.proceedToLogin(updateStep, classToJoin.signupCode)
                    }
                    onSignupWithCode={() =>
                        this.proceedToSignUp(updateStep, classToJoin.signupCode)
                    }
                />
            );
        }
        if (step === "identify") {
            return (
                <SignupIdentifyActionScreen
                    birthdate={birthdate}
                    classCode={this.getClassCode()}
                    continueUrl="/"
                    previousStep="signUp"
                    role="learner"
                    under13={under13}
                    updateStep={updateStep}
                />
            );
        }
        if (step === "login") {
            return (
                <LoginActionScreen
                    continueUrl="/coaches"
                    previousStep="confirmClass"
                    role="learner"
                    updateStep={updateStep}
                    classCode={this.getClassCode()}
                />
            );
        }
        if (step === "intro") {
            return (
                <JoinActionScreen
                    onClassChange={this.handleClassChange}
                    under13={under13}
                    updateStep={updateStep}
                />
            );
        }
        return null;
    };

    render(): React.ReactNode {
        const {className} = this.props;

        // If this component was initialized with class data, skip right to
        // `confirmClass` as the initial step. Otherwise, start with the intro
        // step as usual.
        const initialStep = className ? "confirmClass" : "intro";
        return (
            <ProgressShell
                initialStep={initialStep}
                detailScreen={this.renderDetailScreen}
                actionScreen={this.renderActionScreen}
                customLayoutComponent={JoinPageLayout}
            />
        );
    }
}

export default JoinWidget;
